import { Suspense, lazy, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import cupAnimation from '../assets/lottie/free-coffee-cup.json';

// lottie-react (which bundles lottie-web, ~70KB gzip) is code-split into its own
// chunk so it never lands in the Landing-critical main bundle — it loads only
// when an earned cup actually renders. The static SVG below covers the gap.
const Lottie = lazy(() => import('lottie-react'));

// Each earned cup pops in this many ms after the previous one (left → right).
export const STAMP_STAGGER_MS = 110;
const PRESERVE = 'xMidYMax meet'; // base-align the cup to the bottom of its box
const POP_SETTLE_S = 0.75;        // intro pop duration before the idle bob takes over

/**
 * StaticCup — the branded Penguin Bean takeaway cup at its settled state, drawn
 * inline (no player). Mirrors the Lottie's final frame (same 160×176 viewBox):
 * an all-black cup with a floating lid (gap above it), a white top, an orange
 * bottom strip, and a crisp outline. Used for `prefers-reduced-motion` (the
 * player is never loaded) and as the Suspense fallback while the lottie chunk
 * resolves.
 * @param {string} className - sizing classes for the host box
 */
export function StaticCup({ className }) {
  return (
    <svg viewBox="0 0 160 176" preserveAspectRatio={PRESERVE} className={className} aria-hidden="true">
      <path d="M23 64 L137 64 L122 168 L38 168 Z" fill="#141414" stroke="#000000" strokeWidth="5" strokeLinejoin="round" />
      <path d="M23 64 L137 64 L132.5 95 L27.5 95 Z" fill="#FFFFFF" />
      <path d="M35.5 150 L124.5 150 L122 168 L38 168 Z" fill="#F59E0B" />
      <rect x="11" y="37" width="138" height="18" rx="8" fill="#141414" stroke="#000000" strokeWidth="5" />
      <rect x="26" y="16" width="108" height="28" rx="13" fill="#141414" stroke="#000000" strokeWidth="5" />
      <rect x="66" y="6" width="28" height="16" rx="5" fill="#141414" stroke="#000000" strokeWidth="5" />
    </svg>
  );
}

/**
 * CupPlayer — the lazy Lottie player for one cup. Mounts at frame 0 (cup at
 * scale 0 = invisible), then plays the one-shot bounce+zoom once, staggered by
 * `index × STAMP_STAGGER_MS`, and settles on the last frame (loop disabled, so
 * the settled cup never repaints — the gentle idle bob is a cheap transform on
 * the wrapper, not a Lottie redraw). Fires `onBounce` as it starts (used for the
 * just-earned haptic). Only mounted when motion is allowed.
 */
function CupPlayer({ index, play, onBounce, className }) {
  const ref = useRef(null);
  const firedRef = useRef(false);
  useEffect(() => {
    if (!play || firedRef.current) return;
    firedRef.current = true;
    const t = setTimeout(() => {
      const inst = ref.current;
      if (!inst) return;
      inst.goToAndPlay(0, true);
      if (onBounce) onBounce();
    }, index * STAMP_STAGGER_MS);
    return () => clearTimeout(t);
  }, [play, index, onBounce]);

  return (
    <Lottie
      lottieRef={ref}
      animationData={cupAnimation}
      autoplay={false}
      loop={false}
      className={className}
      rendererSettings={{ preserveAspectRatio: PRESERVE }}
      aria-hidden="true"
    />
  );
}

/**
 * LottieStampCup — one earned coffee in the Free Coffee Tracker, rendered as the
 * branded takeaway-cup Lottie that bounces + zooms in on app open and then keeps
 * a gentle perpetual idle bob. Lazy-loads lottie-react (keeps lottie-web out of
 * the Landing main chunk) with a static inline cup as the fallback; under
 * `reducedMotion` it renders the static cup only (no player, no bob). `play`
 * triggers the intro one-shot; `index` staggers the row left→right; the idle bob
 * is a GPU-cheap transform loop on the wrapper that starts once this cup's intro
 * has settled.
 * @param {number} index - position among the cups (drives the stagger delay)
 * @param {boolean} play - when true, plays the intro then starts the idle bob
 * @param {boolean} reducedMotion - render the static settled cup, no motion
 * @param {() => void} onBounce - fired as the cup starts bouncing (haptic hook)
 * @param {string} className - sizing classes for the host box
 * Used by: components/FreeCoffeeProgress.js (StampSlot, full variant)
 */
export default function LottieStampCup({ index = 0, play = false, reducedMotion = false, onBounce, className = '' }) {
  const cup = reducedMotion ? (
    <StaticCup className="w-full h-full" />
  ) : (
    <Suspense fallback={<StaticCup className="w-full h-full" />}>
      <CupPlayer index={index} play={play} onBounce={onBounce} className="w-full h-full" />
    </Suspense>
  );

  // Reduced motion: static cup, no idle bob.
  if (reducedMotion) return <div className={className}>{cup}</div>;

  // Idle "little bounce": a cheap transform loop that starts once this cup's
  // intro pop has settled, then runs forever.
  return (
    <motion.div
      className={className}
      animate={play ? { y: [0, -3, 0] } : { y: 0 }}
      transition={play
        ? { delay: POP_SETTLE_S + index * (STAMP_STAGGER_MS / 1000), duration: 1.6, repeat: Infinity, ease: 'easeInOut' }
        : { duration: 0 }}
    >
      {cup}
    </motion.div>
  );
}
